import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaHashtag,
  FaSearch,
  FaQrcode,
  FaTimes,
  FaPlus,
  FaMinus,
  FaTrashAlt,
  FaShoppingCart,
  FaCoins,
  FaMoneyCheckAlt,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBoxOpen,
  FaReceipt,
  FaClock,
  FaUserAlt,
} from "react-icons/fa";
import apiRequest from "../services/api";
import { showError } from "../components/dialogHelper";
import QrScannerModal from "../components/QrScannerModal";
import PosOpenModal from "../components/PosOpenModal";
import PosCloseModal from "../components/PosCloseModal";
import PartnerQuickModal from "../components/PartnerQuickModal";

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "BANK", label: "Bank Transfer" },
  { value: "CHAPA", label: "Online Payment" },
];

const etb = (n) => `${(Number(n) || 0).toFixed(2)} ETB`;

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const extractProductList = (res) => {
  const list = res?.data?.content ?? res?.data ?? res ?? [];
  return Array.isArray(list) ? list : [];
};

const searchProducts = async (keyword) => {
  const res = await apiRequest(
    `products/find?keyword=${encodeURIComponent(keyword)}`
  );
  return extractProductList(res);
};

const searchCustomers = async (keyword, page = 0, size = 8) => {
  const queryParams = new URLSearchParams({
    type: "CUSTOMER",
    keyword: keyword.trim(),
    page: page.toString(),
    size: size.toString(),
  });
  const res = await apiRequest(`clients/?${queryParams.toString()}`);
  return {
    items: res?.data?.content || [],
    totalElements: res?.data?.totalElements || 0,
  };
};

const inputCls =
  "w-full rounded-xl px-3 py-2.5 text-xs focus:outline-none transition bg-white border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

const panelCls =
  "backdrop-blur-xl rounded-2xl p-4 sm:p-5 flex flex-col gap-4 border bg-white border-slate-200 shadow-sm dark:bg-slate-900/50 dark:border-white/10";

// --- PRODUCT CARD ---
const ProductCard = ({ product, onAdd }) => {
  const stockRaw = product.availableStock;
  const parsedStock = Number(stockRaw);
  const stock =
    stockRaw === undefined ||
    stockRaw === null ||
    stockRaw === "" ||
    isNaN(parsedStock)
      ? Infinity
      : parsedStock;

  const rawPrice = product.sellingPrice ?? product.price ?? 0;
  const price = isNaN(Number(rawPrice)) ? 0 : Number(rawPrice);

  const isGoods = product.type === "GOODS";
  const isTracked = isGoods && stock !== Infinity;
  const outOfStock = isTracked && stock <= 0;
  const low = isTracked && stock > 0 && stock <= 5;

  return (
    <button
      type="button"
      disabled={outOfStock}
      onClick={() => onAdd(product)}
      className={`group text-left border rounded-xl p-3 transition flex flex-col gap-1.5 ${
        outOfStock
          ? "border-slate-200 bg-slate-50 opacity-40 cursor-not-allowed dark:border-white/5 dark:bg-slate-900/40"
          : "border-slate-200 bg-slate-50 hover:border-emerald-500/40 hover:bg-emerald-50/50 dark:bg-slate-900/40 dark:border-white/5 dark:hover:border-emerald-500/40 dark:hover:bg-slate-900/60"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold leading-snug line-clamp-2 text-slate-800 dark:text-white">
          {product.name || "Unnamed product"}
        </p>
        <FaPlus className="text-emerald-500 dark:text-emerald-400 opacity-0 group-hover:opacity-100 transition text-[10px] shrink-0 mt-0.5" />
      </div>
      <p className="font-mono font-bold text-sm text-emerald-600 dark:text-emerald-400">
        {etb(price)}
      </p>
      <span
        className={`text-[9px] uppercase font-bold tracking-wider ${
          outOfStock
            ? "text-red-600 dark:text-red-400"
            : low
            ? "text-amber-600 dark:text-amber-400"
            : "text-slate-500"
        }`}
      >
        {outOfStock
          ? "Out of stock"
          : stock === Infinity
          ? "In stock"
          : low
          ? `Only ${stock} left`
          : isGoods
          ? `${stock} in stock`
          : ""}
      </span>
    </button>
  );
};

// --- CART ROW ---
const CartRow = ({ item, onQtyChange, onPriceChange, onRemove }) => {
  const isCustomPrice = Number(item.unitPrice) !== Number(item.originalPrice);

  return (
    <div className="flex flex-col gap-2 py-2.5 border-b border-slate-100 last:border-0 dark:border-white/5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold truncate flex-1 min-w-0 text-slate-800 dark:text-white">
          {item.name}
        </p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {item.sku && (
            <span className="text-[10px] font-mono shrink-0 text-slate-500">
              {item.sku}
            </span>
          )}
          <div className="relative shrink-0">
            <input
              type="number"
              min="0"
              step="0.01"
              value={item.unitPrice}
              onChange={(e) => onPriceChange(item.key, e.target.value)}
              className={`w-[72px] border rounded-lg text-right pl-2 pr-1 py-1 font-mono font-bold text-[11px] focus:outline-none bg-white dark:bg-slate-950 ${
                isCustomPrice
                  ? "border-amber-400 text-amber-600 focus:border-amber-500 dark:border-amber-500/40 dark:text-amber-400"
                  : "border-slate-200 text-emerald-600 focus:border-emerald-500/50 dark:border-white/10 dark:text-emerald-400"
              }`}
            />
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => onQtyChange(item.key, item.qty - 1)}
            className="w-6 h-6 rounded-lg flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
          >
            <FaMinus className="text-[9px]" />
          </button>
          <input
            type="number"
            min="1"
            max={item.stock === Infinity ? undefined : item.stock}
            value={item.qty}
            onChange={(e) => onQtyChange(item.key, e.target.value)}
            className="w-10 border rounded-lg text-center text-xs font-mono py-1 focus:outline-none focus:border-emerald-500/50 bg-white border-slate-200 text-slate-800 dark:bg-slate-950 dark:border-white/10 dark:text-white"
          />
          <button
            type="button"
            onClick={() => onQtyChange(item.key, item.qty + 1)}
            className="w-6 h-6 rounded-lg flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
          >
            <FaPlus className="text-[9px]" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => onRemove(item.key)}
          className="text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 transition shrink-0"
        >
          <FaTrashAlt className="text-xs" />
        </button>
      </div>
    </div>
  );
};

// --- MAIN ---
const PosPage = () => {
  const [now, setNow] = useState(new Date());
  const [activeShift, setActiveShift] = useState(null);
  const [isCheckingShift, setIsCheckingShift] = useState(true);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [openingCash, setOpeningCash] = useState("");
  const [countedCash, setCountedCash] = useState("");
  const [terminalId] = useState("POS-01");
  const [isSubmittingShift, setIsSubmittingShift] = useState(false);
  const [shiftError, setShiftError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, 350);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [cart, setCart] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [taxType, setTaxType] = useState("NONE");
  const [partnerQuery, setPartnerQuery] = useState("");
  const debouncedPartnerQuery = useDebouncedValue(partnerQuery, 350);
  const [partnerResults, setPartnerResults] = useState([]);
  const [partnerTotal, setPartnerTotal] = useState(0);
  const [isSearchingPartners, setIsSearchingPartners] = useState(false);
  const [partnerError, setPartnerError] = useState("");
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [applyVat, setApplyVat] = useState(false);
  const [fiscalType, setFiscalType] = useState("MANUAL"); // EIMS | MANUAL | MACHINE
  const [newIrn, setNewIrn] = useState(true);
  const [irn, setIrn] = useState("");
  const [mrc, setMrc] = useState("");

  const [showPartner, setShowPartner] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleOpenPartner = () => setShowPartner(true);
  const handleClosePartner = () => setShowPartner(false);

  const handleCreatePartner = async (partner) => {
    try {
      setSaving(true);
      const res = await apiRequest("clients", {
        method: "POST",
        body: { ...partner, type: "CUSTOMER" },
      });
      const savedPartner = res.data;
      setSelectedPartner(savedPartner);
      setPartnerResults((prev) => [...prev, savedPartner]);
      setShowPartner(false);
    } catch (err) {
      showError(err.message || "Unable to save partner.");
    } finally {
      setSaving(false);
    }
  };

  const [paymentForm, setPaymentForm] = useState({
    method: "BANK",
    amountTendered: "",
    reference: "",
    mobileNumber: "",
    change: "0.0",
  });

  const [showScanner, setShowScanner] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    checkActiveShiftStatus();
  }, []);

  const checkActiveShiftStatus = async () => {
    try {
      setIsCheckingShift(true);
      setShiftError("");
      const res = await apiRequest("pos/active");
      if (res?.data || res) {
        setActiveShift(res.data || res);
        setShowOpenModal(false);
      } else {
        setShowOpenModal(true);
      }
    } catch {
      setActiveShift(null);
      setShowOpenModal(true);
    } finally {
      setIsCheckingShift(false);
    }
  };

  const handleOpenShift = async (e) => {
    e.preventDefault();
    const cashVal = parseFloat(openingCash);
    if (isNaN(cashVal) || cashVal < 0) {
      setShiftError("Please enter a valid starting cash amount.");
      return;
    }
    try {
      setIsSubmittingShift(true);
      setShiftError("");
      const res = await apiRequest(
        `pos/open?terminalId=${terminalId}&openingCash=${cashVal}`,
        { method: "POST" }
      );
      setActiveShift(res.data || res);
      setShowOpenModal(false);
      setOpeningCash("");
      setNotice({
        type: "success",
        message: "Register Drawer shift opened successfully.",
      });
    } catch (err) {
      setShiftError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to open register shift."
      );
    } finally {
      setIsSubmittingShift(false);
    }
  };

  const handleCloseShift = async (e) => {
    e.preventDefault();
    const countVal = parseFloat(countedCash);
    if (isNaN(countVal) || countVal < 0) {
      setShiftError("Please enter a valid total counted physical cash.");
      return;
    }
    try {
      setIsSubmittingShift(true);
      setShiftError("");
      const res = await apiRequest(`pos/close?countedCash=${countVal}`, {
        method: "POST",
      });
      const summary = res.data || res;
      setActiveShift(null);
      setShowCloseModal(false);
      setCountedCash("");
      alert(
        `Shift Terminated Successfully (Z-Report)!\n\n` +
          `Expected Cash in Drawer: ${etb(summary.expectedCash || 0)}\n` +
          `Actual Counted Cash: ${etb(summary.countedCash || 0)}\n` +
          `Variance Calculation: ${etb(summary.difference || 0)}`
      );
      setShowOpenModal(true);
    } catch (err) {
      setShiftError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to close register shift."
      );
    } finally {
      setIsSubmittingShift(false);
    }
  };

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("user") || "{}");
      setTaxRate(parseFloat(parsed?.data?.tenant?.taxRate) || 0);
      setTaxType(parsed?.data?.tenant?.taxType || "NONE");
      setFiscalType(parsed?.data?.tenant?.fiscalType || "MANUAL");
    } catch {
      setTaxRate(0);
      setTaxType("NONE");
      setFiscalType("MANUAL");
    }
  }, []);

  // SALE form: EIMS → system generates IRN (newIrn=true, no user input)
  // MANUAL / MACHINE → user supplies refs (newIrn=false)
  useEffect(() => {
    const isEims = fiscalType === "EIMS";
    setNewIrn(isEims);
    if (isEims) {
      setIrn("");
      setMrc("");
    } else if (fiscalType !== "MACHINE") {
      setMrc("");
    }
  }, [fiscalType]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3000);
    return () => clearTimeout(t);
  }, [notice]);

  useEffect(() => {
    const keyword = debouncedQuery.trim();
    if (!keyword) {
      setResults([]);
      setSearchError("");
      setIsSearching(false);
      return;
    }
    let active = true;
    (async () => {
      setIsSearching(true);
      setSearchError("");
      try {
        const list = await searchProducts(keyword);
        if (active) setResults(list);
      } catch {
        if (active) {
          setSearchError("Could not search products.");
          setResults([]);
        }
      } finally {
        if (active) setIsSearching(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [debouncedQuery]);

  useEffect(() => {
    const keyword = debouncedPartnerQuery.trim();
    if (!keyword || selectedPartner) {
      setPartnerResults([]);
      setPartnerTotal(0);
      setPartnerError("");
      setIsSearchingPartners(false);
      return;
    }
    let active = true;
    (async () => {
      setIsSearchingPartners(true);
      setPartnerError("");
      try {
        const { items, totalElements } = await searchCustomers(keyword);
        if (active) {
          setPartnerResults(items);
          setPartnerTotal(totalElements);
        }
      } catch {
        if (active) {
          setPartnerError("Could not search customers.");
          setPartnerResults([]);
          setPartnerTotal(0);
        }
      } finally {
        if (active) setIsSearchingPartners(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [debouncedPartnerQuery, selectedPartner]);

  const handleSelectPartner = useCallback((client) => {
    setSelectedPartner(client);
    setPartnerQuery("");
    setPartnerResults([]);
    setPartnerTotal(0);
  }, []);

  const handleClearPartner = useCallback(() => {
    setSelectedPartner(null);
    setPartnerQuery("");
    setPartnerResults([]);
    setPartnerTotal(0);
  }, []);

  const addToCart = useCallback((product) => {
    if (!product) return;
    const stockRaw = product.stockQuantity ?? product.quantity ?? product.stock;
    const stock =
      stockRaw === undefined || stockRaw === null ? Infinity : Number(stockRaw);
    if (stock <= 0) {
      setNotice({
        type: "error",
        message: `${product.name || "Item"} is out of stock.`,
      });
      return;
    }
    setCart((prev) => {
      const idx = prev.findIndex((row) => row.productId === product.id);
      if (idx >= 0) {
        const next = [...prev];
        const maxQty = stock === Infinity ? next[idx].qty + 1 : stock;
        next[idx] = { ...next[idx], qty: Math.min(next[idx].qty + 1, maxQty) };
        return next;
      }
      return [
        ...prev,
        {
          key: `${product.id}-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 6)}`,
          productId: product.id,
          name: product.name,
          sku: product.sku || product.barcode || "",
          type: product.type,
          unitPrice: Number(product.sellingPrice ?? product.price ?? 0),
          originalPrice: Number(product.sellingPrice ?? product.price ?? 0),
          qty: 1,
          stock,
        },
      ];
    });
    setNotice({
      type: "success",
      message: `Added ${product.name || "item"} to sale.`,
    });
  }, []);

  const lookupAndAdd = useCallback(
    async (code) => {
      const keyword = (code || "").trim();
      if (!keyword) return false;
      try {
        const list = await searchProducts(keyword);
        if (list.length === 0) {
          setNotice({
            type: "error",
            message: `No product found for "${keyword}".`,
          });
          return false;
        }
        const exactMatch = list.find(
          (p) => p.barcode === keyword || p.sku === keyword
        );
        addToCart(exactMatch || list[0]);
        return true;
      } catch {
        setNotice({ type: "error", message: "Search failed. Try again." });
        return false;
      }
    },
    [addToCart]
  );

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      e.preventDefault();
      lookupAndAdd(searchQuery).then((ok) => {
        if (ok) setSearchQuery("");
      });
    }
  };

  const updateQty = useCallback((key, qty) => {
    setCart((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const n = Math.max(1, Math.round(Number(qty) || 1));
        const max = row.stock !== Infinity ? row.stock : n;
        return { ...row, qty: Math.min(n, max) };
      })
    );
  }, []);

  const updateUnitPrice = useCallback((key, price) => {
    setCart((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const n = Math.max(0, parseFloat(price) || 0);
        return { ...row, unitPrice: n };
      })
    );
  }, []);

  const removeFromCart = useCallback((key) => {
    setCart((prev) => prev.filter((row) => row.key !== key));
  }, []);

  const handleClearSale = useCallback(() => {
    setCart([]);
    setApplyVat(false);
    setSelectedPartner(null);
    setPartnerQuery("");
    setPartnerResults([]);
    setPartnerTotal(0);
    setPaymentForm({
      method: "BANK",
      amountTendered: "",
      reference: "",
      mobileNumber: "",
      change: "0.0",
      applyVat: false,
    });
    setIrn("");
    setMrc("");
    setNewIrn(fiscalType === "EIMS");
    setCheckoutError("");
  }, [fiscalType]);

  const taxLabel = useMemo(() => {
    switch (taxType) {
      case "VAT":
        return `VAT (${taxRate}%)`;
      case "TOT":
        return "Turnover Tax (TOT)";
      default:
        return "Tax";
    }
  }, [taxType, taxRate]);

  const subtotal = useMemo(
    () => cart.reduce((s, r) => s + r.unitPrice * r.qty, 0),
    [cart]
  );

  const taxAmount = useMemo(() => {
    if (!applyVat || taxType === "NONE") return 0;
    return cart.reduce((sum, item) => {
      const amount = item.qty * item.unitPrice;
      let rate = 0;
      if (taxType === "VAT") rate = taxRate;
      else if (taxType === "TOT") rate = item.type === "SERVICE" ? 10 : 2;
      return sum + (amount * rate) / 100;
    }, 0);
  }, [cart, applyVat, taxType, taxRate]);

  const subtotal2 = useMemo(
    () => Math.max(0, subtotal + taxAmount),
    [subtotal, taxAmount]
  );
  const total = useMemo(() => Math.max(0, subtotal2), [subtotal2]);
  const tendered = useMemo(
    () => parseFloat(paymentForm.amountTendered) || 0,
    [paymentForm.amountTendered]
  );
  const change = useMemo(
    () => (paymentForm.method === "CASH" ? Math.max(0, tendered - total) : 0),
    [paymentForm.method, tendered, total]
  );

  const handleCheckout = useCallback(async () => {
    // SALE fiscal rules:
    // EIMS → no user IRN/MRC
    // MANUAL → manual invoice number required → irn
    // MACHINE → FS number + MRC required
    if (fiscalType === "MANUAL" && !irn.trim()) {
      setCheckoutError("Manual invoice number is required.");
      return;
    }
    if (fiscalType === "MACHINE") {
      if (!irn.trim()) {
        setCheckoutError("FS receipt number is required.");
        return;
      }
      if (!mrc.trim()) {
        setCheckoutError("MRC is required.");
        return;
      }
    }
    if (cart.length === 0) return;
    if (paymentForm.method === "CASH" && tendered < total) {
      setCheckoutError("Amount tendered is less than the total due.");
      return;
    }
    setIsCheckingOut(true);
    setCheckoutError("");
    try {
      const reference = [
        paymentForm.reference.trim(),
        paymentForm.method === "MOBILE" && paymentForm.mobileNumber
          ? `MOB:${paymentForm.mobileNumber.trim()}`
          : "",
      ]
        .filter(Boolean)
        .join(" | ");
      const invoiceData = {
        irn: fiscalType === "EIMS" ? "" : irn,
        mrc: fiscalType === "MACHINE" ? mrc : "",
        partner: selectedPartner ? { id: selectedPartner.id } : null,
        items: cart.map((row) => ({
          product: { id: row.productId },
          quantity: row.qty,
          sellingPrice: row.unitPrice,
          itemType: row.type || "GOODS",
        })),
      };
      const queryParams = new URLSearchParams({
        method: paymentForm.method,
        change: change,
        reference,
        applyVat,
      }).toString();
      const res = await apiRequest(`invoices/quick-sale?${queryParams}`, {
        method: "POST",
        body: invoiceData,
      });
      const createdInvoiceNumber =
        res?.data?.invoiceNumber || res?.invoiceNumber;
      handleClearSale();
      setNotice({
        type: "success",
        message: createdInvoiceNumber
          ? `Sale completed — ${createdInvoiceNumber}`
          : "Sale completed.",
      });
    } catch (err) {
      setCheckoutError(
        err?.response?.data?.message || err?.message || "Checkout failed."
      );
    } finally {
      setIsCheckingOut(false);
    }
  }, [
    cart,
    paymentForm,
    total,
    tendered,
    selectedPartner,
    change,
    applyVat,
    handleClearSale,
    irn,
    mrc,
    fiscalType,
  ]);

  const itemCount = useMemo(() => cart.reduce((s, r) => s + r.qty, 0), [cart]);

  if (isCheckingShift) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-xs bg-slate-50 text-slate-500 dark:bg-slate-950 dark:text-slate-400">
        <FaSpinner className="animate-spin text-2xl text-emerald-500 dark:text-emerald-400 mb-2" />
        Verifying terminal drawer session state...
      </div>
    );
  }

  return (
    <div className="min-h-full relative overflow-hidden bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="pointer-events-none fixed -top-40 -left-40 w-96 h-96 rounded-full blur-3xl bg-emerald-500/10 dark:bg-emerald-600/10" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-96 h-96 rounded-full blur-3xl bg-sky-500/10 dark:bg-sky-600/10" />

      <div className="relative max-w-7xl mx-auto p-4 sm:p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl border bg-white border-slate-200 dark:bg-white/5 dark:border-white/10">
              <FaShoppingCart className="text-emerald-600 dark:text-emerald-400 text-lg" />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-wide text-slate-900 dark:text-white">
                Quick Sale
              </h1>
              <p className="text-[11px] font-mono text-slate-500">
                Terminal: {terminalId}
                {activeShift?.cashier?.fullName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {activeShift && (
              <button
                type="button"
                onClick={() => {
                  setShiftError("");
                  setShowCloseModal(true);
                }}
                className="text-xs px-3 py-2 rounded-xl font-medium border transition bg-red-50 text-red-600 border-red-200 hover:bg-red-100 dark:bg-red-600/20 dark:text-red-400 dark:border-red-500/30 dark:hover:bg-red-600/30"
              >
                End Shift
              </button>
            )}
            <div className="flex items-center gap-2 text-[11px] font-mono rounded-xl px-3 py-2 border bg-white border-slate-200 text-slate-500 dark:bg-slate-900/40 dark:border-white/5 dark:text-slate-400">
              <FaClock className="text-slate-400 dark:text-slate-500" />
              {now.toLocaleDateString()}
            </div>
          </div>
        </div>

        {notice && (
          <div
            className={`flex items-center gap-2 text-xs px-4 py-2.5 rounded-xl border ${
              notice.type === "error"
                ? "bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400"
                : "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400"
            }`}
          >
            {notice.type === "error" ? (
              <FaExclamationTriangle />
            ) : (
              <FaCheckCircle />
            )}
            {notice.message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 items-start">
          {/* LEFT */}
          <div className={panelCls}>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xs pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search by name, SKU, barcode, or description…"
                  className={`${inputCls} pl-9 pr-3`}
                />
              </div>
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold flex items-center gap-2 shrink-0 shadow-lg shadow-violet-950/20"
              >
                <FaQrcode /> <span className="hidden sm:inline">Scan</span>
              </button>
            </div>

            {searchError && (
              <p className="text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1.5">
                <FaExclamationTriangle /> {searchError}
              </p>
            )}

            {searchQuery.trim() && (
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                  Search results
                </p>
                {isSearching && (
                  <FaSpinner className="animate-spin text-slate-400 text-xs" />
                )}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {!searchQuery.trim() && !isSearching && (
                <div className="col-span-full flex flex-col items-center justify-center gap-2 py-12 text-slate-500">
                  <FaBoxOpen className="text-2xl" />
                  <p className="text-xs text-center px-6">
                    Search a product or scan its barcode/QR to add it.
                  </p>
                </div>
              )}
              {searchQuery.trim() && results.length === 0 && !isSearching && (
                <div className="col-span-full flex flex-col items-center justify-center gap-2 py-12 text-slate-500">
                  <FaBoxOpen className="text-2xl" />
                  <p className="text-xs">
                    No products found for "{searchQuery.trim()}".
                  </p>
                </div>
              )}
              {results.map((p) => (
                <ProductCard key={p.id} product={p} onAdd={addToCart} />
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div className={`${panelCls} lg:sticky lg:top-4`}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/5">
              <div className="flex items-center gap-2">
                <FaShoppingCart className="text-sky-600 dark:text-sky-400" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
                  Current Sale
                </span>
                {cart.length > 0 && (
                  <span className="text-[10px] rounded-full px-2 py-0.5 font-mono border bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20">
                    {itemCount} items
                  </span>
                )}
              </div>
              {cart.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearSale}
                  className="text-[10px] text-slate-500 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1"
                >
                  <FaTrashAlt /> Clear
                </button>
              )}
            </div>

            <div className="relative">
              {selectedPartner ? (
                <div className="flex items-center justify-between gap-2 rounded-xl pl-3 pr-2 py-2 border bg-sky-50 border-sky-200 dark:bg-slate-950 dark:border-sky-500/30">
                  <span className="flex items-center gap-2 text-xs font-semibold truncate text-slate-800 dark:text-white">
                    <FaUserAlt className="text-sky-600 dark:text-sky-400 text-[10px] shrink-0" />
                    {selectedPartner.name ||
                      selectedPartner.fullName ||
                      `Client #${selectedPartner.id}`}
                  </span>
                  <button
                    type="button"
                    onClick={handleClearPartner}
                    className="text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 transition shrink-0"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              ) : (
                <>
                  <FaUserAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[10px] pointer-events-none" />
                  <input
                    type="text"
                    value={partnerQuery}
                    onChange={(e) => setPartnerQuery(e.target.value)}
                    placeholder="Search customer — leave blank for walk-in"
                    className={`${inputCls} pl-8 pr-20 py-2`}
                  />
                  {isSearchingPartners ? (
                    <FaSpinner className="absolute right-10 top-1/2 -translate-y-1/2 text-slate-400 text-xs animate-spin" />
                  ) : (
                    <button
                      type="button"
                      onClick={handleOpenPartner}
                      title="New Customer"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center transition"
                    >
                      <FaPlus className="text-[10px]" />
                    </button>
                  )}
                  {partnerQuery.trim() && (
                    <div className="absolute z-20 mt-1 w-full rounded-xl shadow-2xl max-h-52 overflow-y-auto border backdrop-blur-xl bg-white border-slate-200 dark:bg-slate-900/95 dark:border-white/10">
                      {partnerError && (
                        <p className="px-3 py-2 text-[11px] text-red-600 dark:text-red-400">
                          {partnerError}
                        </p>
                      )}
                      {!partnerError &&
                        !isSearchingPartners &&
                        partnerResults.length === 0 && (
                          <p className="px-3 py-2 text-[11px] text-slate-500">
                            No customers found
                          </p>
                        )}
                      {partnerResults.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectPartner(client)}
                          className="w-full text-left px-3 py-2 text-xs transition flex items-center justify-between gap-3 text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5"
                        >
                          <span className="truncate">
                            {client.name ||
                              client.fullName ||
                              `Partner #${client.id}`}
                          </span>
                          {(client.phone || client.email) && (
                            <span className="text-[10px] font-mono shrink-0 text-slate-400 dark:text-slate-500">
                              {client.phone || client.email}
                            </span>
                          )}
                        </button>
                      ))}
                      {partnerTotal > partnerResults.length && (
                        <p className="px-3 py-2 text-[10px] text-slate-500 border-t border-slate-100 dark:border-white/5 dark:text-slate-600">
                          {partnerTotal} matches — keep typing to narrow it
                          down.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="max-h-[28vh] overflow-y-auto">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-slate-500">
                  <FaShoppingCart className="text-2xl" />
                  <p className="text-xs">Cart is empty</p>
                </div>
              ) : (
                cart.map((item) => (
                  <CartRow
                    key={item.key}
                    item={item}
                    onQtyChange={updateQty}
                    onPriceChange={updateUnitPrice}
                    onRemove={removeFromCart}
                  />
                ))
              )}
            </div>

            {cart.length > 0 && (
              <>
                <div className="border-t border-slate-100 dark:border-white/5 pt-3 space-y-2 text-xs font-mono">
                  <div className="flex justify-between text-slate-500 dark:text-slate-400">
                    <span>Subtotal</span>
                    <span className="font-bold text-slate-800 dark:text-white">
                      {etb(subtotal)}
                    </span>
                  </div>
                  {taxType !== "NONE" && (
                    <div className="flex justify-between items-center text-slate-500 dark:text-slate-400">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyVat}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setApplyVat(isChecked);
                            setPaymentForm((p) => ({
                              ...p,
                              applyVat: isChecked,
                            }));
                          }}
                          className="accent-emerald-500"
                        />
                        {taxLabel}
                      </label>
                      <span className="font-bold text-emerald-600 dark:text-emerald-400">
                        + {etb(taxAmount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-white/5">
                    <span className="font-bold uppercase text-[11px] text-slate-600 dark:text-slate-300">
                      Total Due
                    </span>
                    <span className="font-bold text-base text-sky-600 dark:text-sky-400">
                      {etb(total)}
                    </span>
                  </div>
                </div>

                <div className="space-y-3 border-t border-slate-200 dark:border-white/10 pt-3">
                  <div className="flex items-center gap-2">
                    <FaMoneyCheckAlt className="text-sky-600 dark:text-sky-400" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
                      Payment
                    </span>
                  </div>
                  <select
                    value={paymentForm.method}
                    onChange={(e) =>
                      setPaymentForm((p) => ({
                        ...p,
                        method: e.target.value,
                        mobileNumber: "",
                      }))
                    }
                    className={inputCls}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>

                  {paymentForm.method === "CASH" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                          Amount Tendered
                        </label>
                        <div className="relative">
                          <FaCoins className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xs pointer-events-none" />
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={paymentForm.amountTendered}
                            onChange={(e) =>
                              setPaymentForm((p) => ({
                                ...p,
                                amountTendered: e.target.value,
                              }))
                            }
                            placeholder={total.toFixed(2)}
                            className={`${inputCls} pl-8 pr-2 font-mono font-bold text-emerald-600 dark:text-emerald-400`}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                          Change
                        </label>
                        <div className="rounded-xl px-3 py-2.5 font-mono font-bold text-xs border bg-slate-50 border-slate-200 text-amber-600 dark:bg-slate-950 dark:border-white/10 dark:text-amber-400">
                          {etb(change)}
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentForm.method === "BANK" && (
                    <input
                      type="text"
                      placeholder="Reference / TXN ID (optional)"
                      value={paymentForm.reference}
                      onChange={(e) =>
                        setPaymentForm((p) => ({
                          ...p,
                          reference: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  )}
                </div>

                {/* Receipt / Fiscal Reference — driven by tenant fiscalType (SALE) */}
                {fiscalType === "EIMS" ? (
                  <div className="space-y-2 w-full pt-1">
                    <div className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                      <FaHashtag className="text-emerald-500" />
                      <span>IRN will be generated by EIMS</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 w-full">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                      Fiscalization Mode
                      <span className="ml-1 text-slate-400 dark:text-slate-600 normal-case font-normal">
                        * {fiscalType}
                      </span>
                    </div>

                    <div
                      className={`grid gap-3 ${
                        fiscalType === "MACHINE"
                          ? "grid-cols-1 sm:grid-cols-2"
                          : "grid-cols-1"
                      }`}
                    >
                      <div className="relative">
                        <FaHashtag className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-amber-500 pointer-events-none" />
                        <input
                          type="text"
                          value={irn}
                          onChange={(e) => {
                            setIrn(e.target.value);
                            setNewIrn(false);
                            if (checkoutError) setCheckoutError("");
                          }}
                          placeholder={
                            fiscalType === "MANUAL"
                              ? "Manual Invoice Number (Required)"
                              : "FS Number (Required)"
                          }
                          className={`${inputCls} pl-9 pr-3 ${
                            checkoutError && !irn.trim()
                              ? "border-red-500 focus:border-red-500"
                              : ""
                          }`}
                        />
                      </div>

                      {fiscalType === "MACHINE" && (
                        <div className="relative">
                          <FaHashtag className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-emerald-500 pointer-events-none" />
                          <input
                            type="text"
                            value={mrc}
                            onChange={(e) => {
                              setMrc(e.target.value);
                              setNewIrn(false);
                              if (checkoutError) setCheckoutError("");
                            }}
                            placeholder="MRC (Required)"
                            className={`${inputCls} pl-9 pr-3 ${
                              checkoutError && !mrc.trim()
                                ? "border-red-500 focus:border-red-500"
                                : ""
                            }`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {checkoutError && (
                  <p className="text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1.5">
                    <FaExclamationTriangle /> {checkoutError}
                  </p>
                )}

                <button
                  type="button"
                  disabled={isCheckingOut}
                  onClick={handleCheckout}
                  className="w-full px-5 py-3 rounded-xl text-sm font-bold transition flex items-center justify-center gap-2 shadow-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
                >
                  {isCheckingOut ? (
                    <>
                      <FaSpinner className="animate-spin" /> Processing…
                    </>
                  ) : (
                    <>
                      <FaReceipt /> Complete Sale • {etb(total)}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showScanner && (
        <QrScannerModal
          onClose={() => setShowScanner(false)}
          onDetect={lookupAndAdd}
        />
      )}
      {showOpenModal && (
        <PosOpenModal
          openingCash={openingCash}
          setOpeningCash={setOpeningCash}
          onSubmit={handleOpenShift}
          isSubmitting={isSubmittingShift}
          shiftError={shiftError}
        />
      )}
      {showCloseModal && (
        <PosCloseModal
          countedCash={countedCash}
          setCountedCash={setCountedCash}
          onSubmit={handleCloseShift}
          isSubmitting={isSubmittingShift}
          shiftError={shiftError}
        />
      )}
      <PartnerQuickModal
        open={showPartner}
        type="CUSTOMER"
        loading={saving}
        onClose={handleClosePartner}
        onSave={handleCreatePartner}
      />
    </div>
  );
};

export default PosPage;
